import { match } from '@frontend/duck-tape';
import { useCallback, useEffect, useReducer, useRef } from 'react';

export const COPY_TO_CLIPBOARD_RESET_DELAY_MS = 3000;

export const COPY_TO_CLIPBOARD_STATUS = {
  copied: 'copied',
  copying: 'copying',
  error: 'error',
  idle: 'idle',
} as const;

export type CopyToClipboardStatus = (typeof COPY_TO_CLIPBOARD_STATUS)[keyof typeof COPY_TO_CLIPBOARD_STATUS];

type CopyToClipboardState = {
  status: CopyToClipboardStatus;
};

type CopyToClipboardEvent =
  | { type: 'copyFailed' }
  | { type: 'copyRequested' }
  | { type: 'copySucceeded' }
  | { type: 'reset' };

export type UseCopyToClipboardOptions = {
  copy: (text: string) => Promise<void>;
  onCopyError?: () => void;
  onCopySuccess?: () => void;
  resetDelayMs?: number;
};

const safelyRunCopyCallback = (callback?: () => void) => {
  try {
    void Promise.resolve(callback?.()).catch(() => undefined);
  } catch {
    // Copy state should not be driven by side-effect callback failures.
  }
};

export const copyToClipboardInitialState: CopyToClipboardState = {
  status: COPY_TO_CLIPBOARD_STATUS.idle,
};

export const copyToClipboardStateMachine = (
  state: CopyToClipboardState,
  event: CopyToClipboardEvent,
): CopyToClipboardState =>
  match(event)
    .with({ type: 'copyRequested' }, () =>
      state.status === COPY_TO_CLIPBOARD_STATUS.idle ? { status: COPY_TO_CLIPBOARD_STATUS.copying } : state,
    )
    .with({ type: 'copySucceeded' }, () =>
      state.status === COPY_TO_CLIPBOARD_STATUS.copying ? { status: COPY_TO_CLIPBOARD_STATUS.copied } : state,
    )
    .with({ type: 'copyFailed' }, () =>
      state.status === COPY_TO_CLIPBOARD_STATUS.copying ? { status: COPY_TO_CLIPBOARD_STATUS.error } : state,
    )
    .with({ type: 'reset' }, () => copyToClipboardInitialState)
    .exhaustive();

export const useCopyToClipboard = ({
  copy,
  onCopyError,
  onCopySuccess,
  resetDelayMs = COPY_TO_CLIPBOARD_RESET_DELAY_MS,
}: UseCopyToClipboardOptions) => {
  const [{ status }, dispatchCopyToClipboardEvent] = useReducer(
    copyToClipboardStateMachine,
    copyToClipboardInitialState,
  );
  const isCopyPendingRef = useRef(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    },
    [],
  );

  const resetAfterDelay = useCallback(() => {
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);

    // eslint-disable-next-line fp/no-mutation
    resetTimeoutRef.current = setTimeout(() => {
      // eslint-disable-next-line fp/no-mutation
      isCopyPendingRef.current = false;
      dispatchCopyToClipboardEvent({ type: 'reset' });
    }, resetDelayMs);
  }, [resetDelayMs]);

  const copyToClipboard = useCallback(
    async (text: string) => {
      if (!text || isCopyPendingRef.current) return false;

      // eslint-disable-next-line fp/no-mutation
      isCopyPendingRef.current = true;
      dispatchCopyToClipboardEvent({ type: 'copyRequested' });

      try {
        await copy(text);
      } catch {
        dispatchCopyToClipboardEvent({ type: 'copyFailed' });
        safelyRunCopyCallback(onCopyError);
        resetAfterDelay();

        return true;
      }

      dispatchCopyToClipboardEvent({ type: 'copySucceeded' });
      safelyRunCopyCallback(onCopySuccess);
      resetAfterDelay();

      return true;
    },
    [copy, onCopyError, onCopySuccess, resetAfterDelay],
  );

  return {
    copyToClipboard,
    isCopied: status === COPY_TO_CLIPBOARD_STATUS.copied,
    isCopying: status === COPY_TO_CLIPBOARD_STATUS.copying,
    isDisabled: status !== COPY_TO_CLIPBOARD_STATUS.idle,
    isError: status === COPY_TO_CLIPBOARD_STATUS.error,
    isLoading: status === COPY_TO_CLIPBOARD_STATUS.copying || status === COPY_TO_CLIPBOARD_STATUS.error,
    status,
  };
};
